import { createSelector, MemoizedSelector, Selector } from '@ngrx/store';
import { CategoryStoreSelectors } from '../category';
import { CategoryType } from '../shared/types';
import { ConcreteCategory } from './category';
import * as RouterStoreSelectors from './router.selector';
import { CategoryEntityMap } from '../category/category.state';
import { Bar, Floor, FrontDesk, Housekeeping, Kiosk, Location, Room, Staff } from '@era/locations-sdk';
import { FloorStoreSelectors } from '../floor';

////////////////////////////////////////////////////////////
// Human ID selectors
////////////////////////////////////////////////////////////
export const selectBrandHumanId: Selector<object, string> = createSelector(
  RouterStoreSelectors.selectParamMap,
  () => 'default-brand'
);

export const selectLocationHumanId: MemoizedSelector<object, string> = RouterStoreSelectors.selectRouterParam('l');

export const selectFloorHumanId: MemoizedSelector<object, string> = RouterStoreSelectors.selectRouterParam('f');

export const selectRoomHumanId: MemoizedSelector<object, string> = RouterStoreSelectors.selectRouterParam('r');

////////////////////////////////////////////////////////////
// Entity selectors
////////////////////////////////////////////////////////////

export const selectLocation: MemoizedSelector<object, Location | undefined> = createSelector(
  selectCategory('Location'),
  (location: ConcreteCategory<'Location'>) => location?.entity
);

export const selectFloor = createSelector(
  selectLocationHumanId,
  selectFloorHumanId,
  FloorStoreSelectors.selectAllFloors,
  (locationHumanId: string, floorHumanId: string, floors: Floor[]): Floor =>
    floors.find((x) => x.id == floorHumanId && x.locationId == locationHumanId)
);

export const selectRoom: MemoizedSelector<object, Room | undefined> = createSelector(
  selectCategory('Room'),
  (room: ConcreteCategory<'Room'>) => room?.entity
);

export const selectBar: MemoizedSelector<object, Bar | undefined> = createSelector(
  selectCategory('Bar'),
  (bar: ConcreteCategory<'Bar'>) => bar?.entity
);

export const selectFrontDesk: MemoizedSelector<object, FrontDesk | undefined> = createSelector(
  selectCategory('FrontDesk'),
  (frontDesk: ConcreteCategory<'FrontDesk'>) => frontDesk?.entity
);

export const selectHousekeeping: MemoizedSelector<object, Housekeeping | undefined> = createSelector(
  selectCategory('Housekeeping'),
  (housekeeping: ConcreteCategory<'Housekeeping'>) => housekeeping?.entity
);

export const selectKiosk: MemoizedSelector<object, Kiosk | undefined> = createSelector(
  selectCategory('Kiosk'),
  (kiosk: ConcreteCategory<'Kiosk'>) => kiosk?.entity
);

export const selectStaff: MemoizedSelector<object, Staff | undefined> = createSelector(
  selectCategory('Staff'),
  (staff: ConcreteCategory<'Staff'>) => staff?.entity
);

////////////////////////////////////////////////////////////
// Unique ID selectors
////////////////////////////////////////////////////////////
export const selectBrandId: MemoizedSelector<object, string> = createSelector(
  RouterStoreSelectors.selectParamMap,
  () => 'default-brand'
);

export const selectLocationId: MemoizedSelector<object, string> = createSelector(
  selectCategory('Location'),
  (location: ConcreteCategory<'Location'>) => location?.categoryId
);

export const selectFloorId: MemoizedSelector<object, string> = createSelector(selectFloor, (floor) => floor?.uniqueId);

export const selectRoomId: MemoizedSelector<object, string> = createSelector(
  selectCategory('Room'),
  (room: ConcreteCategory<'Room'>) => room?.categoryId
);

export const selectBarId: MemoizedSelector<object, string> = createSelector(
  selectCategory('Bar'),
  (bar: ConcreteCategory<'Bar'>) => bar?.categoryId
);

export const selectFrontDeskId: MemoizedSelector<object, string> = createSelector(
  selectCategory('FrontDesk'),
  (frontDesk: ConcreteCategory<'FrontDesk'>) => frontDesk?.categoryId
);

export const selectHousekeepingId: MemoizedSelector<object, string> = createSelector(
  selectCategory('Housekeeping'),
  (housekeeping: ConcreteCategory<'Housekeeping'>) => housekeeping?.categoryId
);

export const selectKioskId: MemoizedSelector<object, string> = createSelector(
  selectCategory('Kiosk'),
  (kiosk: ConcreteCategory<'Kiosk'>) => kiosk?.categoryId
);

export const selectStaffId: MemoizedSelector<object, string> = createSelector(
  selectCategory('Staff'),
  (staff: ConcreteCategory<'Staff'>) => staff?.categoryId
);

// TODO, move
export const selectClientConfigurationId: Selector<object, string> =
  RouterStoreSelectors.selectRouterParam('api-client-settings-id');

export const selectInRoomCredentialsId: Selector<object, string> =
  RouterStoreSelectors.selectRouterParam('in-room-credentials-id');

// Function overloads for type narrowing
export function selectCategory(categoryType: 'Brand'): MemoizedSelector<object, ConcreteCategory<'Brand'> | undefined>;
export function selectCategory(
  categoryType: 'Location'
): MemoizedSelector<object, ConcreteCategory<'Location'> | undefined>;
export function selectCategory(categoryType: 'Room'): MemoizedSelector<object, ConcreteCategory<'Room'> | undefined>;
export function selectCategory(categoryType: 'Bar'): MemoizedSelector<object, ConcreteCategory<'Bar'> | undefined>;
export function selectCategory(
  categoryType: 'FrontDesk'
): MemoizedSelector<object, ConcreteCategory<'FrontDesk'> | undefined>;
export function selectCategory(
  categoryType: 'Housekeeping'
): MemoizedSelector<object, ConcreteCategory<'Housekeeping'> | undefined>;
export function selectCategory(categoryType: 'Kiosk'): MemoizedSelector<object, ConcreteCategory<'Kiosk'> | undefined>;
export function selectCategory(categoryType: 'Staff'): MemoizedSelector<object, ConcreteCategory<'Staff'> | undefined>;
export function selectCategory(categoryType: 'None'): MemoizedSelector<object, ConcreteCategory<'None'> | undefined>;
export function selectCategory<T extends CategoryType>(
  categoryType: T
): MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
export function selectCategory<T extends CategoryType>(
  categoryType: T
): MemoizedSelector<object, CategoryEntityMap[T] | undefined> {
  const brandId = 'default-brand';

  switch (categoryType) {
    case 'Brand':
      return createSelector(
        () =>
          new ConcreteCategory(
            'Brand',
            brandId,
            brandId,
            'Default Brand',
            { id: brandId, displayName: 'Default Brand', uniqueId: brandId, humanId: brandId },
            undefined
          )
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'Location':
      return createSelector(
        selectLocationHumanId,
        CategoryStoreSelectors.selectAllCategoriesByType('Location'),
        (locationHumanId: string, locations: ConcreteCategory<'Location'>[]) =>
          locations.find((x) => x.categoryHumanId === locationHumanId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'Room':
      return createSelector(
        selectCategory('Location'),
        CategoryStoreSelectors.selectAllCategoriesByType('Room'),
        selectRoomHumanId,
        (location: ConcreteCategory<'Location'>, rooms: ConcreteCategory<'Room'>[], roomHumanId: string) =>
          rooms.find((x) => x.categoryHumanId === roomHumanId && x.parent?.categoryId === location?.categoryId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'Bar':
      return createSelector(
        selectCategory('Location'),
        CategoryStoreSelectors.selectAllCategoriesByType('Bar'),
        (location: ConcreteCategory<'Location'>, bars: ConcreteCategory<'Bar'>[]) =>
          bars.find((x) => x.parent?.categoryId === location?.categoryId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'FrontDesk':
      return createSelector(
        selectCategory('Location'),
        CategoryStoreSelectors.selectAllCategoriesByType('FrontDesk'),
        (location: ConcreteCategory<'Location'>, frontdesks: ConcreteCategory<'FrontDesk'>[]) =>
          frontdesks.find((x) => x.parent?.categoryId === location?.categoryId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'Housekeeping':
      return createSelector(
        selectCategory('Location'),
        CategoryStoreSelectors.selectAllCategoriesByType('Housekeeping'),
        (location: ConcreteCategory<'Location'>, housekeepings: ConcreteCategory<'Housekeeping'>[]) =>
          housekeepings.find((x) => x.parent?.categoryId === location?.categoryId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'Kiosk':
      return createSelector(
        selectCategory('Location'),
        CategoryStoreSelectors.selectAllCategoriesByType('Kiosk'),
        (location: ConcreteCategory<'Location'>, kiosks: ConcreteCategory<'Kiosk'>[]) =>
          kiosks.find((x) => x.parent?.categoryId === location?.categoryId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'Staff':
      return createSelector(
        selectCategory('Location'),
        CategoryStoreSelectors.selectAllCategoriesByType('Staff'),
        (location: ConcreteCategory<'Location'>, staffs: ConcreteCategory<'Staff'>[]) =>
          staffs.find((x) => x.parent?.categoryId === location?.categoryId)
      ) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
    case 'None':
    default:
      return createSelector(() => undefined) as MemoizedSelector<object, CategoryEntityMap[T] | undefined>;
  }
}

export const selectBundleId: Selector<object, string> = RouterStoreSelectors.selectRouterParam('b');
